import { useRef, useState } from "react";
import useSWR from "swr";
import { Trash2, Upload, Loader2, FolderPlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { fmtSize } from "@/lib/format";
import { Card, btn, inputCls } from "@/components/common/Ornament";

function Folders({ folders, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/folders", { name: name.trim() }); setName(""); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (f) => {
    const n = window.prompt("Rename folder", f.name);
    if (n && n.trim()) { await api.put(`/admin/folders/${f.id}`, { name: n.trim() }); reload(); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete folder “${f.name}”? Its documents become Unfiled.`)) return;
    await api.delete(`/admin/folders/${f.id}`); reload();
  };
  return (
    <Card eyebrow="Organize" title="Folders" testId="admin-folders">
      <div className="flex flex-wrap gap-2 mb-4">
        {folders.map((f, i) => (
          <span key={f.id} className="inline-flex items-center gap-1 rounded-full border border-[#E2DCD2] bg-[#F9F6F0] pl-3 pr-1 py-1 text-sm" data-testid={`admin-folder-${i}`}>
            {f.name}
            <button onClick={() => rename(f)} className="p-1 text-slate-400 hover:text-[#0F2E23]" data-testid={`admin-folder-rename-${i}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={() => del(f)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`admin-folder-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New folder name" className={inputCls + " sm:w-64"} data-testid="admin-folder-name-input" />
        <button onClick={add} className={btn.ghost} data-testid="admin-folder-add-btn"><FolderPlus className="h-4 w-4" /> Add</button>
      </div>
    </Card>
  );
}

function DocRow({ d, i, folders, onSave, onDelete }) {
  const [name, setName] = useState(d.name);
  return (
    <tr className="border-b border-[#E2DCD2]" data-testid={`admin-doc-row-${i}`}>
      <td className="py-2 pr-2"><input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== d.name && onSave(d, { name: name.trim() })} className={inputCls + " !h-9 min-w-[220px]"} data-testid={`admin-doc-name-${i}`} /></td>
      <td className="py-2 px-2 text-xs uppercase font-mono-num text-slate-500 whitespace-nowrap">{d.ext} · {fmtSize(d.size)}</td>
      <td className="py-2 px-2">
        <select value={d.folder_id || ""} onChange={(e) => onSave(d, { folder_id: e.target.value || null })} className={inputCls + " !h-9 min-w-[160px]"} data-testid={`admin-doc-folder-${i}`}>
          <option value="">Unfiled</option>
          {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </td>
      <td className="py-2 px-2 text-center"><Switch checked={d.visible} onCheckedChange={(v) => onSave(d, { visible: v })} data-testid={`admin-doc-visible-${i}`} /></td>
      <td className="py-2 px-2 text-center"><Switch checked={d.downloadable} onCheckedChange={(v) => onSave(d, { downloadable: v })} data-testid={`admin-doc-downloadable-${i}`} /></td>
      <td className="py-2 pl-2 text-right"><button onClick={() => onDelete(d)} className={btn.danger} data-testid={`admin-doc-delete-${i}`}><Trash2 className="h-4 w-4" /></button></td>
    </tr>
  );
}

export default function DocumentsTab() {
  const { data: folders = [], mutate: mf } = useSWR("/folders");
  const { data: docs = [], mutate } = useSWR("/documents");
  const [folder, setFolder] = useState("");
  const [visible, setVisible] = useState(true);
  const [downloadable, setDownloadable] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const upload = async (files) => {
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file); fd.append("folder_id", folder); fd.append("visible", visible); fd.append("downloadable", downloadable);
        await api.post("/admin/documents", fd);
      }
      toast.success(`${files.length} document(s) uploaded`);
      mutate();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const onSave = async (d, patch) => {
    try { await api.put(`/admin/documents/${d.id}`, { name: d.name, folder_id: d.folder_id, visible: d.visible, downloadable: d.downloadable, ...patch }); mutate(); toast.success("Document updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async (d) => {
    if (!window.confirm(`Delete “${d.name}”?`)) return;
    await api.delete(`/admin/documents/${d.id}`); mutate(); toast.success("Document deleted");
  };

  return (
    <div className="space-y-6">
      <Folders folders={folders} reload={() => { mf(); mutate(); }} />
      <Card eyebrow="Data Room" title="Upload & Manage Documents" testId="admin-docs">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6 rounded-md bg-[#F9F6F0] border border-[#E2DCD2] p-3">
          <select value={folder} onChange={(e) => setFolder(e.target.value)} className={inputCls + " lg:w-56"} data-testid="admin-doc-upload-folder">
            <option value="">Unfiled</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm"><Switch checked={visible} onCheckedChange={setVisible} data-testid="admin-doc-upload-visible" /> Visible to viewers</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={downloadable} onCheckedChange={setDownloadable} data-testid="admin-doc-upload-downloadable" /> Allow download</label>
          <input ref={ref} type="file" multiple hidden accept=".pdf,.docx,.xlsx,.csv,.jpg,.jpeg,.png,.mp4" onChange={(e) => e.target.files.length && upload([...e.target.files])} data-testid="admin-doc-file-input" />
          <button onClick={() => ref.current.click()} disabled={busy} className={btn.primary + " lg:ml-auto"} data-testid="admin-doc-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload files</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="border-b border-[#0F2E23] text-left text-xs uppercase tracking-wider text-[#3E5C4E]">
              <th className="py-2 pr-2">Name</th><th className="py-2 px-2">Type</th><th className="py-2 px-2">Folder</th><th className="py-2 px-2 text-center">Visible</th><th className="py-2 px-2 text-center">Download</th><th />
            </tr></thead>
            <tbody>{docs.map((d, i) => <DocRow key={d.id + d.name} d={d} i={i} folders={folders} onSave={onSave} onDelete={onDelete} />)}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
